// src/components/auth/Login.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Logo } from '../../hooks/useLogo';

const Login = () => {
  usePageTitle('Login');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading, error } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember: false
  });

  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Handle verification messages from email links
  useEffect(() => {
    const verificationType = searchParams.get('verification');
    const message = searchParams.get('message');
    const verified = searchParams.get('verified');

    if (verificationType && message) {
      const decodedMessage = decodeURIComponent(message);
      showNotification(verificationType, decodedMessage);

      // Clear URL parameters
      navigate('/login', { replace: true });
    } else if (verified === 'true') {
      showNotification('success', 'Email verified successfully! You can now log in to your account.');

      // Clear URL parameters
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate]);

  // Show message if user was force-logged-out due to organization suspension
  useEffect(() => {
    const logoutMessage = sessionStorage.getItem('haazri_logout_message');
    if (logoutMessage) {
      showNotification('error', logoutMessage);
      sessionStorage.removeItem('haazri_logout_message');
    }
  }, []);



  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Don't clear auth errors here - let them persist until form submission
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = 'Username or email is required';
    }

    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  // Updated handleSubmit function for Login.js
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const response = await login(formData.username, formData.password);

      if (response?.user?.license_expired) {
        showNotification('info', "Your organization's subscription has expired. Please renew to continue using eHAAZRI.");
        navigate('/subscriptions');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);

      // Check for specific organization approval messages
      const errorMessage = err.message || '';

      if (errorMessage.includes('pending approval')) {
        // Show a more detailed pending approval message
        showNotification('info', 'Your organization is still pending approval. Our team is reviewing your registration and you will receive an email notification once approved. This typically takes 1-2 business days.');
      } else if (errorMessage.includes('rejected')) {
        // Show rejection message with contact info
        showNotification('error', 'Your organization registration has been rejected. Please contact our support team at support@ehaazri.com for more information.');
      } else if (errorMessage.includes('suspended')) {
        // Show organization suspension message
        showNotification('error', 'Your organization has been suspended. Please contact support@ehaazri.com for assistance.');
      } else if (errorMessage.includes('not active')) {
        // Show general account issue message
        showNotification('error', 'Your organization account is inactive. Please contact support@ehaazri.com for assistance.');
      }

      // Error is also handled by the auth context for display in the form
    }
  };

  // Enhanced notification function to handle 'info' type
  const showNotification = (type, message) => {
    const notification = document.createElement('div');
    notification.className = `notification ${type}`;

    const iconMap = {
      'success': '✓',
      'error': '✗',
      'info': 'ℹ'
    };

    const colorMap = {
      'success': { bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
      'error': { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' },
      'info': { bg: '#cce7ff', text: '#004085', border: '#b3d9ff' }
    };

    const colors = colorMap[type] || colorMap['info'];

    notification.innerHTML = `
    <div class="notification-content">
      <span class="notification-icon">${iconMap[type] || 'ℹ'}</span>
      <span class="notification-message">${message}</span>
      <button class="notification-close" onclick="this.parentElement.parentElement.remove()">×</button>
    </div>
  `;

    // Add styles
    const styles = `
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    background: ${colors.bg};
    color: ${colors.text};
    border: 1px solid ${colors.border};
    border-radius: 8px;
    padding: 16px;
    max-width: 450px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    animation: slideIn 0.3s ease-out;
    line-height: 1.5;
  `;

    notification.style.cssText = styles;

    // Add to document
    document.body.appendChild(notification);

    // Auto remove after 8 seconds for info messages, 5 for others
    const autoRemoveTime = type === 'info' ? 8000 : 5000;
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, autoRemoveTime);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-left">
          <div className="admin-icon">
            <i className="fas fa-user-shield"></i>
          </div>
          <h2>Welcome Back</h2>
          <p>Sign in to access your eHAAZRI dashboard and manage your organization's attendance</p>
          <div className="admin-badge">
            <i className="fas fa-shield-alt"></i> Secure Access
          </div>
        </div>

        <div className="login-right">
          <Logo size="medium" usePublicLogo={true} />

          <form className="login-form" onSubmit={handleSubmit}>
            <h3>Sign In</h3>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="username">Username or Email</label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={formErrors.username ? 'form-input error' : 'form-input'}
                  placeholder="Enter your username or email"
                  required
                />
              </div>
              {formErrors.username && (
                <span className="form-error">{formErrors.username}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <i className="fas fa-lock input-icon"></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={formErrors.password ? 'form-input error' : 'form-input'}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                </button>
              </div>
              {formErrors.password && (
                <span className="form-error">{formErrors.password}</span>
              )}
            </div>

            <div className="form-options">
              <label className="remember-me">
                <input
                  type="checkbox"
                  id="remember"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                />
                <span>Remember me</span>
              </label>
              <Link to="/forgot-password" className="forgot-password">
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Signing In...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt"></i>
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="divider">
            <span>or</span>
          </div>

          <div className="register-link">
            Don't have an account?
            <Link to="/register">Register Organization</Link>
          </div>
        </div>
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          height: 100%;
          background: var(--gradient);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          flex: 1;
        }

        .login-container {
          background: #ffffff;
          border-radius: 24px;
          box-shadow: 0 24px 48px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          max-width: 900px;
          width: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          min-height: 550px;
          backdrop-filter: blur(10px);
        }

        .login-left {
          background: var(--gradient);
          color: var(--white);
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
          text-align: center;
          position: relative;
        }

        .login-left::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="4" height="4" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="0.5" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          opacity: 0.3;
        }

        .admin-icon {
          width: 80px;
          height: 80px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          font-size: 2rem;
          position: relative;
          z-index: 1;
        }

        .login-left h2 {
          font-size: 2rem;
          margin-bottom: 1rem;
          font-weight: 700;
          position: relative;
          z-index: 1;
        }

        .login-left p {
          font-size: 1.1rem;
          opacity: 0.9;
          position: relative;
          z-index: 1;
        }

        .admin-badge {
          background: rgba(255,255,255,0.2);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
          margin-top: 1rem;
          display: inline-block;
          position: relative;
          z-index: 1;
        }

        .login-right {
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 2rem;
          justify-content: center;
        }

        .logo-icon {
          width: 40px;
          height: 40px;
          background: var(--gradient);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
        }

        .logo-text {
          font-size: 1.5rem;
          font-weight: bold;
          background: var(--gradient);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .login-form h3 {
          color: var(--text-dark);
          margin-bottom: 2rem;
          text-align: center;
          font-size: 1.5rem;
        }

        .error-message {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          padding: 1rem 1.25rem;
          border-radius: 10px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border-left: 4px solid #ef4444;
          font-weight: 500;
          font-size: 0.9rem;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
          animation: slideDown 0.3s ease-out;
        }

        .error-message i {
          font-size: 1.1rem;
          flex-shrink: 0;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          color: var(--text-dark);
          font-weight: 600;
          font-size: 0.9rem;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          color: #9ca3af;
          font-size: 1rem;
          z-index: 1;
          pointer-events: none;
        }

        .form-group input {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          outline: none;
          background: #ffffff;
        }

        .form-group input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }

        .form-group input:focus + .input-icon,
        .input-wrapper:focus-within .input-icon {
          color: #3b82f6;
        }

        .form-group input.error {
          border-color: #ef4444;
        }

        .form-error {
          color: #ef4444;
          font-size: 0.8rem;
          margin-top: 0.4rem;
          display: block;
          font-weight: 500;
        }

        .password-toggle {
          position: absolute;
          right: 0.75rem;
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 0.5rem;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          border-radius: 6px;
        }

        .password-toggle:hover {
          color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
        }

        .password-toggle i {
          font-size: 0.95rem;
        }

        .form-options {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.75rem;
        }

        .remember-me {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #4b5563;
          font-size: 0.9rem;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .remember-me:hover {
          color: #1f2937;
        }

        .remember-me input[type="checkbox"] {
          width: 1rem;
          height: 1rem;
          cursor: pointer;
          accent-color: #3b82f6;
        }

        .forgot-password {
          color: #3b82f6;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .forgot-password:hover {
          color: #2563eb;
          text-decoration: underline;
        }

        .spinner {
          width: 1rem;
          height: 1rem;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 0.5rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .divider {
          text-align: center;
          margin: 1.5rem 0;
          position: relative;
          color: var(--text-light);
        }

        .divider::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: #e0e0e0;
        }

        .divider span {
          background: var(--white);
          padding: 0 1rem;
        }

        .register-link {
          text-align: center;
          color: var(--text-light);
        }

        .register-link a {
          color: var(--primary-color);
          text-decoration: none;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        .register-link a:hover {
          text-decoration: underline;
        }

        @media (max-width: 768px) {
          .login-container {
            grid-template-columns: 1fr;
            max-width: 400px;
          }

          .login-left {
            display: none;
          }

          .login-right {
            padding: 2rem;
          }
        }

        
      `}</style>
    </div>
  );
};

export default Login;